import { Api } from "../net/api";
import { skinText } from "../display-names.generated";
import { setArtIndex } from "./card";

/**
 * THE CARD DATABASE, as the client reads it.
 *
 * 🚨 **THE ORIGINAL'S FIELD NAMES ARE INVERTED, AND THIS IS THE ONE PLACE THAT MATTERS.** From
 * CLAUDE.md, and confirmed against the served database:
 *
 *     card.title       = "Claw and Tear"                      <- the NAME
 *     card.description = "Rage"                               <- the EFFECT. MECHANICS.
 *     card.tooltip     = "A coordinated beast attack that..." <- FLAVOUR
 *
 * **Mapping "new description" onto `description` rewrites every card's rules and looks like a
 * reskin.** The contract cannot be cleaned up - `Revival.Core.dll` deserialises those exact names -
 * so the defect is made unreachable instead: nothing outside this file touches the raw field names,
 * and the accessors below are called `name`, `effect` and `flavour`.
 *
 * ⭐ **TITLES COME FROM THE SERVER, SO A RENAME NEEDS NO CLIENT RELEASE.** The de-brand renames the
 * LABEL, never the KEY - `revival-display-names` - and this reads whatever the server currently
 * serves.
 */

interface CardRecord {
  id?: string;
  title?: string;
  description?: string;
  tooltip?: string;
  artUrl?: string;
  mana?: number;
  /** How many copies of this card one deck may hold. From the served database. */
  limitInDeck?: number;
  /** Hero only: the ability, its cost in foxfire, and what it does. */
  heroPowerName?: string;
  heroPowerCost?: number;
  heroPowerDescription?: string;
  /** Effect config ids the engine attaches to this object. A hero's power is the first. */
  effectsIds?: string[];
  /** Heroes only: the engine's own switch for a hero that is not in play. */
  isDisabled?: boolean;
  attack?: number;
  hp?: number;
  type?: string;
  /**
   * The card's REALM. Mechanical, not decorative: typed Foxfire makes the realm the thing that pays
   * for the card, so it decides the frame the player reads the cost against.
   */
  quadrant?: string;
  /**
   * The card's FACTION - who it belongs to in the world.
   *
   * ⚠️ **THE VALUE IS THE ORIGINAL STUDIO'S IDENTIFIER AND MUST NEVER BE PRINTED RAW.** A faction is
   * an enum the engine deserialises, so it cannot be renamed in the data; nine of our ten factions
   * still carry their original key (`Company_of_Hoplites`, `Iskandrian_Hegemony`, ...). Always pass
   * it through `displayName()` from `display-names.generated.ts`, which the theme workbook owns.
   */
  factions?: string[];

  /**
   * The card's SUBTYPES - the tribes it belongs to (`Taunt`, `Undead`, `Summon`, ...).
   *
   * ⭐ **THIS IS THE AXIS SYNERGY KEYS ON**, via an effect's `targetSubTypes`, and the engine
   * matches it with OR semantics so one effect can reward a whole cluster
   * (`Revival.Core.decompiled.cs:12999`). See `.claude/skills/revival-realm-identity/SKILL.md`.
   *
   * ⚠️ `"None"` is the unset sentinel, not a tribe, and `"ExFlag"` is an engine marker. Both must be
   * filtered out before anything is shown to a player - `cardSubTypes()` does it once, here, so no
   * call site has to remember.
   */
  subTypes?: string[];
}

/**
 * 🚨 **THE THREE SECTIONS SHARE AN ID NAMESPACE, AND MERGING THEM CORRUPTED 40 OF 171 CARDS.**
 *
 * Measured 2026-08-14: `cards`, `heroes` and `ironsides` all number their entries from 1, so
 * **40 ids are defined twice** - `id "11"` is the card *Ambush Wolf* AND the ironside *Coffinbite*.
 * The loader wrote all three lists into one map in order, so ironsides (last) WON:
 *
 *     cardName("11")   -> "Coffinbite"          (should be "Ambush Wolf")
 *     cardRealm("11")  -> "The_Spirit_Veil"     (should be "The_Verdant_Hollow")
 *     cardArtUrl("11") -> Coffinbite's art
 *
 * So roughly a quarter of the collection rendered under another entity's name, art and REALM -
 * on the board as well as in the builder. It surfaced as a false "nemesis card in this deck"
 * warning, which is the only reason it was ever noticed.
 *
 * ⭐ **SEPARATE MAPS, AND CARDS WIN.** A lookup resolves against cards first. Hero ids are strings
 * (`hero_arcadia_asclepius`) so they cannot collide; ironsides are consulted last and only for an
 * id no card claims - and the ironside layer is CUT by decision anyway
 * (`ProgressionStore`: *"THE IRONSIDE LAYER IS CUT, BY DECISION"*).
 *
 * ⛔ **NEVER GO BACK TO ONE MAP.** `tools/verify_card_ids.py` fails the build on any id that two
 * sections claim and the client resolves the wrong way.
 */
const cardsById = new Map<string, CardRecord>();
const heroesById = new Map<string, CardRecord>();
const ironsidesById = new Map<string, CardRecord>();

/** Resolve an id the way the game means it: a card, else a hero, else an ironside. */
function lookup(dataId: string | undefined): CardRecord | undefined {
  if (dataId === undefined || dataId === null) return undefined;
  const key = String(dataId);
  return cardsById.get(key) ?? heroesById.get(key) ?? ironsidesById.get(key);
}

let loaded = false;

export async function loadCardNames(): Promise<void> {
  if (loaded) return;
  try {
    const db = (await Api.gameDatabase()) as { cards?: CardRecord[]; heroes?: CardRecord[]; ironsides?: CardRecord[] };
    const sections: Array<[CardRecord[] | undefined, Map<string, CardRecord>]> = [
      [db.cards, cardsById],
      [db.heroes, heroesById],
      [db.ironsides, ironsidesById],
    ];
    for (const [list, target] of sections) {
      if (!Array.isArray(list)) continue;
      for (const c of list) if (c.id) target.set(String(c.id), c);
    }
    loaded = true;

    // ⚠️ Counted and reported, because a silent collision is what cost 40 cards their identity.
    let shadowed = 0;
    for (const id of ironsidesById.keys()) if (cardsById.has(id)) shadowed++;
    for (const id of heroesById.keys()) if (cardsById.has(id)) shadowed++;
    if (shadowed) {
      console.info(`[cards] ${shadowed} id(s) are claimed by more than one section; ` +
                   `cards win, as the game means them`);
    }

    // ⭐ THE ART INDEX IS BUILT HERE, keyed by `artUrl` - the only key that resolves (171 of 171,
    // against ZERO for `title`). Handed to the card renderer so nothing else re-derives it.
    // ⚠️ Built in the SAME precedence as `lookup`, or the art would disagree with the name.
    const index = new Map<string, string>();
    for (const map of [ironsidesById, heroesById, cardsById]) {
      for (const [id, rec] of map) if (rec.artUrl) index.set(id, rec.artUrl);
    }
    setArtIndex(index);

    const total = cardsById.size + heroesById.size + ironsidesById.size;
    console.info(`[cards] ${total} record(s) loaded (${cardsById.size} cards), ` +
                 `${index.size} with art`);
  } catch (e) {
    // ⚠️ NOT FATAL, AND SAID SO. A match with unnamed cards is playable and obviously degraded; a
    // match that refuses to start because a name lookup failed is not.
    console.error(
      "[cards] the card database did not load, so cards will show their ids. This is a DEGRADED " +
        "board, not a broken one:",
      e,
    );
  }
}

/** The `artUrl` for a card or hero id - the ONLY key that resolves (171/171 vs 0 by title). */
export function cardArtUrl(dataId: string | undefined): string | undefined {
  if (!dataId) return undefined;
  return lookup(dataId)?.artUrl;
}

/** The card's NAME. Never `description` - that is the effect. */
/**
 * The card's realm, or undefined if it has none.
 *
 * ⚠️ **NEVER DEFAULTED TO A REALM.** An unknown realm must render as unknown; substituting Neutral
 * would produce a card that misreports the one fact its cost is priced on, and looks deliberate
 * doing it. Realms are also going to arrive as NFTs, so "the set of realms" is not knowable here.
 */
/**
 * The raw record, for callers that need more than a name.
 *
 * ⚠️ Returned READ-ONLY by convention: it is the shared database row, not a copy. A caller that
 * mutated it would edit every other reader's card.
 */
export function cardRecord(dataId: string | undefined): Readonly<CardRecord> | undefined {
  return lookup(dataId);
}

/**
 * Every hero in the database, for the deck builder's hero picker.
 *
 * ⚠️ Read from the HEROES section only - `lookup()` resolves cards first by design, so asking it
 * for "all heroes" would be asking the wrong map.
 */
export function allHeroes(): Array<Readonly<CardRecord>> {
  return [...heroesById.values()].filter((h) => !h.isDisabled);
}

/**
 * Every CARD in the database - not heroes, not ironsides.
 *
 * ⭐ The deck builder lists the whole set and marks what you do not own, so a player can SEE the
 * card they are missing. `bypassCardOwnership` is false, so seeing is all they can do with it - and
 * that is the point: an invisible card cannot be wanted.
 */
export function allCards(): Array<Readonly<CardRecord>> {
  return [...cardsById.values()];
}

/**
 * ⭐ **"NEUTRAL" HAS ONE OWNER, AND IT DID NOT UNTIL 2026-08-22.**
 *
 * `verify_open_sets.py` found the literal `"Neutral"` compared in **nine** places across
 * `match/card.ts`, `screens/match.ts` and `screens/lore.ts`. Nothing was broken - every comparison
 * was correct - but §3b's question answers badly: *to change this one fact, how many places must
 * change?* **Nine.** A realm that arrives from the DATA (a realm minted as an NFT, say) will never
 * appear in a literal written today, and the card renders perfectly while wearing the wrong realm,
 * which is the failure mode that looks deliberate.
 *
 * ⚠️ **NEUTRAL IS A SENTINEL, NOT A REALM**, and that is exactly why it kept being written out by
 * hand: it is the ABSENCE of a realm, so it reads as a natural literal at every call site. Naming
 * it here makes it one fact with one owner, and the gate can then see that the set is closed.
 */
export const NEUTRAL = "Neutral";

/** Is this realm the neutral sentinel - i.e. the card belongs to no realm at all? */
export function isNeutral(realm: string | undefined | null): boolean {
  return !realm || realm === NEUTRAL;
}

/**
 * The positive form, and **a type predicate on purpose**.
 *
 * ⚠️ Every call site read `realm && realm !== "Neutral"`, where the `realm &&` was doing TWO jobs:
 * excluding the sentinel AND narrowing `string | undefined` to `string` for the code after it.
 * Replacing that with a plain boolean helper compiles the exclusion and silently drops the
 * narrowing, which surfaced as four TS2345/TS18048 errors on lines nobody had touched. A predicate
 * keeps both, so the collapse to one owner costs no type safety.
 */
export function hasRealm(realm: string | undefined | null): realm is string {
  return !!realm && realm !== NEUTRAL;
}

export function cardRealm(dataId: string | undefined): string | undefined {
  const q = lookup(dataId)?.quadrant;
  return q || undefined;
}

/**
 * The card's faction identifier, or undefined. RAW - the caller must render it via `displayName`.
 *
 * ⭐ An accessor rather than `rec["factions"]?.[0]` at each call site: the served field is an ARRAY
 * and every reader would otherwise re-decide what a multi-faction card means.
 */
export function cardFaction(dataId: string | undefined): string | undefined {
  const f = cardRecord(dataId)?.factions?.[0];
  return f && f !== "None" ? f : undefined;
}

/**
 * The card's TRIBES, cleaned. RAW keys - the caller renders them via `displayName`.
 *
 * ⛔ **A TRIBE THE PLAYER CANNOT SEE IS NOT IDENTITY.** The card face rendered name, cost, art,
 * rules, attack, health, realm and faction, and never these - so a synergy payoff that counts
 * `Undead` gave the player nothing to count. This project has paid for that exact shape twice
 * already: the de-branded faction names were correct in a file and rendered nowhere, and three
 * player answers sat in `title` tooltips nobody reads.
 *
 * ⚠️ Filters `"None"` (the unset sentinel) and `"ExFlag"` (an engine marker) once, here.
 */
export function cardSubTypes(dataId: string | undefined): string[] {
  const s = cardRecord(dataId)?.subTypes;
  if (!Array.isArray(s)) return [];
  return s.filter((x) => x && x !== "None" && x !== "ExFlag");
}

export function cardName(dataId: string | undefined): string {
  if (!dataId) return "?";
  return lookup(dataId)?.title || `#${dataId}`;
}

/**
 * The card's EFFECT - the rules text. This is the field the original calls `description`.
 *
 * 🔴 **SKINNED HERE, AT THE ACCESSOR, SO NO CALL SITE CAN FORGET.** Rules text names other cards and
 * tribes mid-sentence, and `displayName` cannot help: it matches the WHOLE string, which is right
 * for an identifier and useless inside a sentence.
 *
 * ⛔ **AND THIS WAS NOT ONLY A BRAND LEAK, IT WAS INCOHERENT.** Card 120's title is already
 * de-branded to "Old Grizzly, Forest Protector" while its own rules text still read *"Other Beast
 * you control have +0/+2 while **Kallisto** is in play"* - the card referred to a name that appears
 * nowhere in the game. Measured 2026-08-20: **17 player-readable strings** carried Kallisto, Chiron,
 * Myrmidon, Kobaloi, centaur or Arctus.
 *
 * ⚠️ Unity's `RevivalTextSkin` sweeper has always done this; the web client, which is the product,
 * had no equivalent. See `.claude/skills/revival-display-names/SKILL.md`.
 */
export function cardEffect(dataId: string | undefined): string {
  if (!dataId) return "";
  return skinText(lookup(dataId)?.description ?? "");
}

/** The card's FLAVOUR text. This is the field the original calls `tooltip`. */
export function cardFlavour(dataId: string | undefined): string {
  if (!dataId) return "";
  /* Skinned for the same reason as the rules text: flavour is prose a player reads, and it names
   * characters and places mid-sentence where `displayName` cannot reach. */
  return skinText(lookup(dataId)?.tooltip ?? "");
}
